/**
 * PIPELINE LIST — elenco delle pipeline, paginato e ordinato server-side.
 *
 * Pattern di riferimento: `QuotationList.tsx`, ridotto: la pipeline è entità di
 * configurazione, senza join con altre entità né colonne contribuite dai plugin.
 * Il name apre il form di modifica (non c'è una pagina di dettaglio separata).
 */

import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { type GridColDef } from '@mui/x-data-grid';
import { getEntityLabelByFqn } from '../../entityRegistry/entityRegistry';
import { useIsClassNameDisabled } from '../../plugin/useCoreItems';
import { useServerSearch, useServerListParams, useListDeleteHandler } from '@crm/shared';
import { ServerDataGrid } from '../../components/ServerDataGrid';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useConfirmDialog } from '../../components/useConfirmDialog';
import { SearchGlyph, PlusGlyph, EditGlyph, TrashGlyph } from '../../components/icons';
import { fetchPipelines, deletePipeline } from './pipelineThunks';
import type { PipelineSliceState } from './pipelineTypes';

export default function PipelineList() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const confirm = useConfirmDialog();
  const { items: allItems, status, error, totalCount: serverTotal } = useAppSelector(
    (state) => state.pipelines as PipelineSliceState,
  );
  // Le pipeline di un tipo spento dalla pagina «Voci del CRM» (FILO-53) non si elencano.
  const isClassNameDisabled = useIsClassNameDisabled();
  const items = useMemo(
    () => allItems.filter((p) => !isClassNameDisabled(p.className)),
    [allItems, isClassNameDisabled],
  );
  // ponytail: il totale del server meno le pipeline nascoste di QUESTA pagina; per un conteggio
  // esatto servirebbe il filtro lato server.
  const totalCount = serverTotal - (allItems.length - items.length);

  // Ricerca server-side condivisa (min 3 caratteri + debounce 300ms).
  const { search, setSearch, query } = useServerSearch();
  const {
    paginationModel, setPaginationModel,
    sortModel, setSortModel,
    resetPage,
    backendParams,
  } = useServerListParams();

  // Reset a pagina 1 quando cambia il filtro.
  useEffect(() => { resetPage(); }, [query, resetPage]);

  useEffect(() => {
    dispatch(fetchPipelines({ ...backendParams, search: query || undefined }));
  }, [backendParams, query, dispatch]);

  // Delete + refetch coerente con totalCount/paginazione (vedi TS-9).
  const handleDelete = useListDeleteHandler({
    deleteThunk: deletePipeline,
    fetchThunk: fetchPipelines,
    fetchParams: { ...backendParams, search: query || undefined },
    itemsLength: items.length,
    page: paginationModel.page,
    resetPage,
    dispatch,
  });

  const columns = useMemo(() => pipelineColumns(confirm, handleDelete, t), [confirm, handleDelete, t]);

  if (status === 'failed') return <p className="text-error">{t('common.error', { message: error })}</p>;

  return (
    <div>
      {/* Testata come la lista Contatti, che e' la pagina di riferimento del
          nuovo design: titolo e conteggio a sinistra, ricerca e azione a
          destra, il tutto fuori dalla card. */}
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('pipeline.title')}</h1>
          <span className="page-sub">{t('common.record', { count: totalCount })}</span>
        </div>
        <div className="page-actions">
          <div className="searchline">
            <SearchGlyph />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('pipeline.searchPlaceholder')}
              aria-label={t('pipeline.searchPlaceholder')}
            />
          </div>
          <Link to="/settings/pipelines/new" className="btn-primary">
            <PlusGlyph /> {t('pipeline.newPipeline')}
          </Link>
        </div>
      </div>

      <div className="list-card">
        {status === 'loading' && <p className="list-feedback">{t('common.loading')}</p>}
        {status === 'succeeded' && items.length === 0 && (
          <p className="text-muted list-feedback">{t('pipeline.noPipelines')}</p>
        )}
        {items.length > 0 && (
          <ServerDataGrid
            rows={items}
            columns={columns}
            getRowId={(row) => row.pipelineId}
            rowCount={totalCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            loading={status === 'loading'}
            getRowHeight={() => 64}
            className="tk-grid"
          />
        )}
      </div>

      <ConfirmDialog open={confirm.isOpen} message={confirm.message} onConfirm={confirm.confirm} onCancel={confirm.cancel} />
    </div>
  );
}

function pipelineColumns(
  confirm: ReturnType<typeof useConfirmDialog>,
  handleDelete: (pipelineId: number) => void,
  t: (key: string, opts?: Record<string, unknown>) => string,
): GridColDef[] {
  return [
    {
      field: 'name', headerName: t('pipeline.columns.name'), flex: 1,
      renderCell: (params) => (
        <span className="lc-name">
          <Link to={`/settings/pipelines/${params.row.pipelineId}/edit`} className="nm">{params.value}</Link>
        </span>
      ),
    },
    {
      // TS-117: il tipo di entità del funnel. Non ordinabile come description — il backend accetta
      // il sort solo su name/createDate/modifiedDate — e mostrato come etichetta di dominio, non
      // come FQN: "Preventivo" dice qualcosa a chi guarda, il nome della classe Java no.
      field: 'className', headerName: t('pipeline.columns.entityType'), width: 160, sortable: false,
      renderCell: (params) => getEntityLabelByFqn(params.value as string, t),
    },
    {
      // Non ordinabile: il backend accetta il sort solo su name/createDate/modifiedDate.
      field: 'description', headerName: t('pipeline.columns.description'), flex: 1, sortable: false,
      renderCell: (params) => (params.value ? (params.value as string) : '—'),
    },
    {
      // Derivata dall'array stati: nessun sort server-side possibile.
      field: 'states', headerName: t('pipeline.columns.states'), width: 120, sortable: false,
      align: 'right', headerAlign: 'right',
      valueGetter: (_value: unknown, row: { states?: unknown[] }) => row.states?.length ?? 0,
      renderCell: (params) => <span className="cell-mono">{params.value as number}</span>,
    },
    {
      // Azioni a icona come nella lista contatti.
      field: 'actions', headerName: t('pipeline.columns.actions'), sortable: false,
      width: 100, align: 'right', headerAlign: 'right',
      renderCell: (params) => (
        <span className="lc-actions">
          <Link
            to={`/settings/pipelines/${params.row.pipelineId}/edit`}
            className="lc-act"
            aria-label={t('common.edit')}
          >
            <EditGlyph />
          </Link>
          <button
            type="button"
            className="lc-act del"
            aria-label={t('common.delete')}
            onClick={() => confirm.requestConfirm(
              t('pipeline.deleteConfirm', { name: params.row.name }),
              () => handleDelete(params.row.pipelineId),
            )}
          >
            <TrashGlyph />
          </button>
        </span>
      ),
    },
  ];
}
